import {
  createRef,
  forwardRef,
  KeyboardEvent,
  useCallback,
  useImperativeHandle,
  useState
} from "react";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import SelectBoxItem from "./item";
import BjelinButton from "../BjelinButton";
import Icon from "../Icon";
import { get } from "lodash";
import "./style.scss";

export type SelectBoxResultHandle = {
  resetActiveItem: () => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
};

export type SelectBoxResultProps = {
  query: string;
  searchResult: object[];
  ItemComponent: React.FunctionComponent<{ item: object }>;
  itemIdField: string | string[];
  allowCustomInput?: boolean;
  placeholder?: string;
  hintText?: string;
  noMatchText?: string;
  onChange: (value: object | false) => void;
  itemClassName?: string;
  compact?: boolean;
};

const SelectBoxResult = forwardRef<SelectBoxResultHandle, SelectBoxResultProps>(
  (props: SelectBoxResultProps, ref) => {
    ref = ref || createRef();
    const {
      query,
      searchResult,
      ItemComponent,
      itemIdField = "id",
      allowCustomInput = false,
      placeholder = "",
      hintText = "",
      noMatchText = "",
      onChange,
      itemClassName = "",
      compact = false
    } = props;
    const t = useBjelinTranslations();

    const [activeItemId, setActiveItemId] = useState(0); //Active row for keyboard navigation

    const setValue = useCallback(
      (id: number | string | false) => {
        if (id) {
          const activeIndex = id
            ? searchResult.findIndex(p => get(p, itemIdField) === id)
            : -1;
          const item = searchResult[activeIndex];
          if (item) {
            onChange?.({ ...item });
          } else if (allowCustomInput) {
            const itemIdKey = Array.isArray(itemIdField)
              ? itemIdField.join(".")
              : itemIdField;
            onChange?.({ [itemIdKey]: id });
          } else {
            onChange?.(false);
          }
        } else {
          onChange?.(false);
        }
      },
      [searchResult, allowCustomInput, itemIdField, onChange]
    );

    const resetActiveItem = () => {
      setActiveItemId(0);
    };

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
          case "Escape":
            setActiveItemId(0);
            break;
          case "Enter":
            if (searchResult.length > 0) {
              if (!activeItemId) {
                const item = searchResult[0];
                setActiveItemId(get(item, itemIdField));
              } else {
                setValue(activeItemId);
              }
            }
            e.preventDefault();
            break;
          case "ArrowDown":
          case "Tab":
            if (searchResult.length > 0) {
              const activeIndex = activeItemId
                ? searchResult.findIndex(
                    p => get(p, itemIdField) === activeItemId
                  )
                : -1;
              const item =
                searchResult[(activeIndex + 1) % searchResult.length];
              setActiveItemId(get(item, itemIdField));
            }
            e.preventDefault();
            break;
          case "ArrowUp":
            if (searchResult.length > 0) {
              const activeIndex = activeItemId
                ? searchResult.findIndex(
                    p => get(p, itemIdField) === activeItemId
                  )
                : -1;
              const item =
                searchResult[
                  (activeIndex + searchResult.length - 1) % searchResult.length
                ];
              setActiveItemId(get(item, itemIdField));
            }
            e.preventDefault();
            break;
          case "ArrowLeft":
          case "ArrowRight":
            break;
          case "Backspace":
          case "Delete":
          default:
        }
      },
      [activeItemId, itemIdField, searchResult, setValue]
    );

    useImperativeHandle(ref, () => ({
      resetActiveItem,
      handleKeyDown
    }));

    return (
      <div
        role="listbox"
        aria-readonly
        id="select-box-result"
        className="select-box-result"
        style={{
          flex: "0 1 100%",
          overflowY: "auto"
        }}
      >
        {searchResult.length > 0 ? (
          <>
            {searchResult.map(item => {
              const itemIdValue = get(item, itemIdField);
              return (
                <SelectBoxItem
                  key={itemIdValue}
                  id={itemIdValue}
                  item={item}
                  ItemComponent={ItemComponent}
                  searchResultMode
                  active={activeItemId === itemIdValue}
                  setSelected={setValue}
                  className={itemClassName}
                  compact={compact}
                />
              );
            })}
          </>
        ) : (
          (query.length <= 2 || !allowCustomInput) && (
            <div
              role="alert"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <div style={{ margin: "1rem" }}>
                <Icon name="circle-info" />{" "}
                <span>
                  {query.length > 2 ? (
                    !allowCustomInput && noMatchText
                  ) : (
                    <>
                      {placeholder}. {hintText}
                    </>
                  )}
                </span>
              </div>
            </div>
          )
        )}
        {allowCustomInput && query.length > 2 && (
          <div
            role="alert"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <div style={{ margin: "1rem" }}>
              <Icon name="circle-info" />{" "}
              <span>{t("search.no-match.custom-input")}</span>
            </div>

            <div style={{ margin: "1rem" }}>
              <BjelinButton
                onClick={() => {
                  setTimeout(() => {
                    setValue(query);
                  }, 1);
                }}
                color={"primary"}
                icon={"chevron-right"}
                icon_position={"after"}
              >
                {t("continue")}
              </BjelinButton>
            </div>
          </div>
        )}
      </div>
    );
  }
);
SelectBoxResult.displayName = "SelectBoxResult";

export default SelectBoxResult;
